<template>
    <div id="user__profile__modal">
        <div>
            <b-tabs content-class="mt-3">
                <!-- Profile Tab -->
                <b-tab title="Profile" active>
                    <ValidationObserver v-slot="{ handleSubmit }">
                        <form @submit.prevent="handleSubmit(saveUser)">
                            <div class="row">
                                <div v-show="error" class="col-md-12 mb-3">
                                    <div class="alert alert-danger">Email address already in use. Please try a different
                                        email address.</div>
                                </div>
                                <div v-show="errorPw" class="col-md-12 mb-3">
                                    <div class="alert alert-danger" v-html="errorMessage"></div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>First Name</label>
                                        <input type="text" v-model="loggedInUserData.first_name" class="form-control"
                                            ref="focusThis" />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>Last Name</label>
                                        <input type="text" v-model="loggedInUserData.last_name" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-12">
                                    <div class="form-group">
                                        <label>Email*</label>
                                        <ValidationProvider mode="passive" rules="required|email" v-slot="{ errors }">
                                            <input type="text" v-model="loggedInUserData.email" class="form-control"
                                                :readonly="loggedInUserData.id != '' && loggedInUserData.email != ''" />
                                            <span class="text-danger" v-for="(error, index) in errors" :key="index">{{
                                                error }}</span>
                                        </ValidationProvider>
                                    </div>
                                </div>

                                <div class="col-md-12">
                                    <div class="form-group">
                                        <a href="#" class="" :class="psExpanded ? 'bordered-link' : ''"
                                            @click.prevent="toggleRows">
                                            <span> Set New Password
                                                <i v-if="psExpanded" class="fas fa-chevron-up"></i>
                                                <i v-else class="fas fa-chevron-down"></i>
                                            </span>
                                        </a>
                                    </div>
                                </div>
                                <transition name="fade">
                                    <div class="row m-0" v-if="psExpanded">
                                        <div class="col-md-12 ">
                                            <div class="row bordered-parent">
                                                <!-- enter current password if editing password for current user -->
                                                <div class="col-md-12 pr-0 pl-0"
                                                    v-if="psExpanded && loggedInUserCompare">
                                                    <div class="form-group">
                                                        <label>Current Password*</label>
                                                        <ValidationProvider mode="passive"
                                                            :rules="password != '' ? 'required' : ''"
                                                            v-slot="{ errors }">
                                                            <input :type="showCurrentPassword ? 'text' : 'password'"
                                                                v-model="currentPassword" class="form-control" />
                                                            <span class="toggle-password top-55"
                                                                @click="toggleCurrentPassword">
                                                                <i v-if="showCurrentPassword" class="fas fa-eye"></i>
                                                                <i v-else class="fas fa-eye-slash"></i>
                                                            </span>
                                                            <span class="text-danger" v-for="(error, index) in errors"
                                                                :key="index">{{ error }}</span>
                                                        </ValidationProvider>
                                                    </div>
                                                </div>
                                                <div class="col-md-12 pr-0 pl-0" v-if="psExpanded">
                                                    <div class="form-group">
                                                        <label>New Password*</label>
                                                        <ValidationProvider mode="passive"
                                                            :rules="!loggedInUserData.id ? 'required' : ''"
                                                            v-slot="{ errors }">
                                                            <input :type="showPassword ? 'text' : 'password'"
                                                                v-model="password" class="form-control"
                                                                @input="validatePassword($event.target.value)" />
                                                            <span class="toggle-password top-50"
                                                                @click="togglePassword">
                                                                <i v-if="showPassword" class="fas fa-eye"></i>
                                                                <i v-else class="fas fa-eye-slash"></i>
                                                            </span>
                                                            <password v-model="password" :strength-meter-only="true"
                                                                style="max-width: unset; margin: 0;" />
                                                            <span class="text-danger" v-for="(error, index) in errors"
                                                                :key="index">{{ error }}</span>
                                                        </ValidationProvider>
                                                    </div>
                                                </div>
                                                <div class="col-md-12 pr-0 pl-0" v-if="psExpanded">
                                                    <ul class="password-checklist">
                                                        <li
                                                            :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('min') == 'pass', 'error': checkPwConditionReached('min') == 'err' }">
                                                            <span class="pw-list-check"><i
                                                                    :class="`fas ${getPwListIcon('min')}`"></i></span>
                                                            At least 8 letters
                                                        </li>
                                                        <li
                                                            :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('uppercase') == 'pass', 'error': checkPwConditionReached('uppercase') == 'err' }">
                                                            <span class="pw-list-check"><i
                                                                    :class="`fas ${getPwListIcon('uppercase')}`"></i></span>
                                                            At least 1 capital letters
                                                        </li>
                                                        <li
                                                            :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('lowercase') == 'pass', 'error': checkPwConditionReached('lowercase') == 'err' }">
                                                            <span class="pw-list-check"><i
                                                                    :class="`fas ${getPwListIcon('lowercase')}`"></i></span>
                                                            At least 1 lower case letters
                                                        </li>
                                                        <li
                                                            :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('symbols') == 'pass', 'error': checkPwConditionReached('symbols') == 'err' }">
                                                            <span class="pw-list-check"><i
                                                                    :class="`fas ${getPwListIcon('symbols')}`"></i></span>
                                                            At least 1 special characters (!$#@%^*&amp;{}[]&lt;&gt;)
                                                        </li>
                                                        <li
                                                            :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('digits') == 'pass', 'error': checkPwConditionReached('digits') == 'err' }">
                                                            <span class="pw-list-check"><i
                                                                    :class="`fas ${getPwListIcon('digits')}`"></i></span>
                                                            At least 1 numberic digits
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </transition>

                                <div class="col-md-12">
                                    <div class="form-group">
                                        <label>Roles*</label>
                                        <ValidationProvider mode="passive" rules="required" v-slot="{ errors }">
                                            <multiselect v-model="loggedInUserData.userRoles" :show-labels="false"
                                                track-by="id" label="name" :options="allRoles" :multiple="true"
                                                :disabled="loggedInUserCompare"></multiselect>
                                            <span class="text-danger">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                    </div>
                                </div>
                                <div v-if="!enablePasswordEdit && !loggedInUserCompare" class="col-md-12">
                                    <div class="form-group">
                                        <label>Active: <switches v-model="loggedInUserData.is_enabled" theme="bootstrap"
                                                color="primary"></switches></label>
                                    </div>
                                </div>
                                <div class="col-md-12">
                                    <div class="d-flex update-profile-controls">
                                        <div class="form-group">
                                            <button class="btn btn-primary" type="submit"
                                                :disabled="isSubmitting">Save</button>
                                        </div>
                                        <!-- 
                            <div class="form-group">
                                <a href="#" @click="toggleEnablePasswordEdit">
                                    <span> Change password</span>
                                    <span v-if="enablePasswordEdit">Back to profile</span>
                                </a>
                            </div>
                             -->
                                    </div>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </b-tab>

                <!-- Deliverables Tab -->
                <b-tab title="Deliverables">
                    <div class="row">
                        <div class="col-md-12">
                            <div class="form-group user--preferences--multiselect">
                                <label>Preferences</label>
                                <div>
                                    <span v-show="!editing" @click="showEditPreference">
                                        <span
                                            v-if="!preferenceData.deliverable_type || !preferenceData.deliverable_type.length"
                                            class="exclude-outside-click cursor-pointer">
                                            Click to set deliverable type preference
                                        </span>
                                        <span v-else class="exclude-outside-click badge badge-gray cursor-pointer"
                                            :key="'user__preference__' + index + '__' + Math.random()"
                                            v-for="(preference, index) in preferenceData.deliverable_type">
                                            {{ preference.name }}
                                        </span>
                                    </span>
                                    <multiselect ref="userDeliverableType" v-click-outside="outside"
                                        v-model="preferenceData.deliverable_type" :show-labels="false"
                                        :options="preferenceDeliverableTypes" :multiple="true" v-show="editing"
                                        label="name" track-by="id" :close-on-select="false"></multiselect>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <div class="d-flex update-profile-controls">
                                <div class="form-group">
                                    <button class="btn btn-primary" @click="saveUser"
                                        :disabled="isSubmitting">Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab>

                <!-- Notifications Tab -->
                <b-tab title="Notifications">
                    <div class="notification-preferences">
                        <div v-if="hasNoPreferences" class="text-center py-5">
                            <p class="text-muted">No notification preferences found for your account.</p>
                            <!-- <p class="text-muted">Please contact an administrator to set up your notification preferences.</p> -->
                        </div>

                        <div v-else>
                            <p class="text-muted mb-3">Choose which notifications you want to receive:</p>

                            <!-- Project Notifications -->
                            <div v-if="notificationPreferences.project && notificationPreferences.project.length"
                                class="preference-section mb-4">
                                <h5 class="section-title">Project Notifications</h5>
                                <div class="table-responsive">
                                    <table class="table table-sm preference-table">
                                        <thead>
                                            <tr>
                                                <th>{{ notificationHeaderLabels.type }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.mineTooltip">
                                                    {{ notificationHeaderLabels.mine }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.othersTooltip">
                                                    {{ notificationHeaderLabels.others }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="pref in notificationPreferences.project" :key="pref.id">
                                                <td>{{ formatAlertType(pref.type) }}</td>
                                                <td class="text-center">
                                                    <b-form-checkbox v-model="pref.notify_mine"
                                                        @change="updatePreference(pref)" />
                                                </td>
                                                <td class="text-center">
                                                    <span v-b-tooltip.hover="othersTooltip(pref)">
                                                        <b-form-checkbox v-model="pref.notify_others"
                                                            :disabled="isOthersDisabled(pref)"
                                                            @change="updatePreference(pref)" />
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Deliverable/Task Notifications -->
                            <div v-if="notificationPreferences.deliverable && notificationPreferences.deliverable.length"
                                class="preference-section mb-4">
                                <h5 class="section-title">Deliverable Notifications</h5>
                                <div class="table-responsive">
                                    <table class="table table-sm preference-table">
                                        <thead>
                                            <tr>
                                                <th>{{ notificationHeaderLabels.type }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.mineTooltip">
                                                    {{ notificationHeaderLabels.mine }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.othersTooltip">
                                                    {{ notificationHeaderLabels.others }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="pref in notificationPreferences.deliverable" :key="pref.id">
                                                <td>{{ formatAlertType(pref.type) }}</td>
                                                <td class="text-center">
                                                    <b-form-checkbox v-model="pref.notify_mine"
                                                        @change="updatePreference(pref)" />
                                                </td>
                                                <td class="text-center">
                                                    <span v-b-tooltip.hover="othersTooltip(pref)">
                                                        <b-form-checkbox v-model="pref.notify_others"
                                                            :disabled="isOthersDisabled(pref)"
                                                            @change="updatePreference(pref)" />
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Task Notifications -->
                            <div v-if="notificationPreferences.task && notificationPreferences.task.length"
                                class="preference-section mb-4">
                                <h5 class="section-title">Task Notifications</h5>
                                <div class="table-responsive">
                                    <table class="table table-sm preference-table">
                                        <thead>
                                            <tr>
                                                <th>{{ notificationHeaderLabels.type }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.mineTooltip">
                                                    {{ notificationHeaderLabels.mine }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.othersTooltip">
                                                    {{ notificationHeaderLabels.others }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="pref in notificationPreferences.task" :key="pref.id">
                                                <td>{{ formatAlertType(pref.type) }}</td>
                                                <td class="text-center">
                                                    <b-form-checkbox v-model="pref.notify_mine"
                                                        @change="updatePreference(pref)" />
                                                </td>
                                                <td class="text-center">
                                                    <span v-b-tooltip.hover="othersTooltip(pref)">
                                                        <b-form-checkbox v-model="pref.notify_others"
                                                            :disabled="isOthersDisabled(pref)"
                                                            @change="updatePreference(pref)" />
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Chat Notifications -->
                            <div v-if="notificationPreferences.chat && notificationPreferences.chat.length"
                                class="preference-section mb-4">
                                <h5 class="section-title">Chat Notifications</h5>
                                <div class="table-responsive">
                                    <table class="table table-sm preference-table">
                                        <thead>
                                            <tr>
                                                <th>{{ notificationHeaderLabels.type }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.mineTooltip">
                                                    {{ notificationHeaderLabels.mine }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.othersTooltip">
                                                    {{ notificationHeaderLabels.others }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="pref in notificationPreferences.chat" :key="pref.id">
                                                <td>{{ formatAlertType(pref.type) }}</td>
                                                <td class="text-center">
                                                    <b-form-checkbox v-model="pref.notify_mine"
                                                        @change="updatePreference(pref)" />
                                                </td>
                                                <td class="text-center">
                                                    <span v-b-tooltip.hover="othersTooltip(pref)">
                                                        <b-form-checkbox v-model="pref.notify_others"
                                                            :disabled="isOthersDisabled(pref)"
                                                            @change="updatePreference(pref)" />
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Other Notifications -->
                            <div v-if="notificationPreferences.ungrouped && notificationPreferences.ungrouped.length"
                                class="preference-section">
                                <h5 class="section-title">Other Notifications</h5>
                                <div class="table-responsive">
                                    <table class="table table-sm preference-table">
                                        <thead>
                                            <tr>
                                                <th>{{ notificationHeaderLabels.type }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.mineTooltip">
                                                    {{ notificationHeaderLabels.mine }}</th>
                                                <th class="text-center" :title="notificationHeaderLabels.othersTooltip">
                                                    {{ notificationHeaderLabels.others }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="pref in notificationPreferences.ungrouped" :key="pref.id">
                                                <td>{{ formatAlertType(pref.type) }}</td>
                                                <td class="text-center">
                                                    <b-form-checkbox v-model="pref.notify_mine"
                                                        @change="updatePreference(pref)" />
                                                </td>
                                                <td class="text-center">
                                                    <span v-b-tooltip.hover="othersTooltip(pref)">
                                                        <b-form-checkbox v-model="pref.notify_others"
                                                            :disabled="isOthersDisabled(pref)"
                                                            @change="updatePreference(pref)" />
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab>
            </b-tabs>
        </div>
    </div>
</template>

<script>
import passwordValidator from 'password-validator';
import Password from 'vue-password-strength-meter';
import Switches from 'vue-switches';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import toast from "../../toast";

export default {
    data() {
        return {
            errorMessage: '',
            currentPassword: '',
            password: '',
            confirmPassword: '',
            editing: false,
            error: false,
            enablePasswordEdit: true,
            psExpanded: false,
            pwValidator: null,
            pwValidateList: [],
            pwValidateErrorList: [],

            errorPw: false,
            showCurrentPassword: false,
            showPassword: false,
            isSubmitting: false,

            // Notification preferences
            notificationPreferences: {
                project: [],
                deliverable: [],
                task: [],
                chat: [],
                ungrouped: [],
            },
            notificationHeaderLabels: {
                type: 'Notification Type',
                mine: 'For Me',
                others: 'For Others',
                mineTooltip: 'Changes that affect you directly.',
                othersTooltip: 'Other changes within projects or deliverables you are part of.',
            },
        }
    },
    components: {
        Switches,
        Password
    },
    computed: {
        ...mapGetters(['preferenceData', 'preferenceDeliverableTypes', 'loggedInUserData', 'allRoles']),
        loggedInUserCompare() {
            return localStorage.getItem('userId') == this.loggedInUserData.id;
        },
        hasNoPreferences() {
            return (!this.notificationPreferences.project || !this.notificationPreferences.project.length) &&
                (!this.notificationPreferences.deliverable || !this.notificationPreferences.deliverable.length) &&
                (!this.notificationPreferences.task || !this.notificationPreferences.task.length) &&
                (!this.notificationPreferences.chat || !this.notificationPreferences.chat.length) &&
                (!this.notificationPreferences.ungrouped || !this.notificationPreferences.ungrouped.length);
        }
    },
    methods: {
        ...mapActions(['savePreference']),
        ...mapMutations(['setDataLoading']),
        outside() {
            this.editing = false;
        },
        showEditPreference() {
            this.editing = true
            this.$nextTick(() => {
                this.$refs.userDeliverableType.$el.focus()
            })
        },
        saveUser() {
            this.pwValidateErrorList = this.pwValidator.validate(this.password, { list: true });
            if (this.pwValidateErrorList.length > 0 && (this.password || this.currentPassword)) {
                return;
            }

            this.isSubmitting = true;
            if (this.password) {
                this.loggedInUserData.password = this.password;
                this.loggedInUserData.currentPassword = this.currentPassword;
            }
            this.loggedInUserData.preferences = this.preferenceData.deliverable_type;
            let that = this;
            that.setDataLoading(true);
            this.$store.dispatch("updateProfile", this.loggedInUserData).then(function (resp) {
                that.isSubmitting = false;
                that.errorMessage = "Invalid password";
                if (resp.data == false) {
                    that.error = true;
                } else if (resp.data == 'invalid_password') {
                    that.errorPw = true;
                } else if (resp.data == 'invalid_password') {
                    that.errorPw = true;
                } else if (resp.data == 'invalid_current_password') {
                    that.hasError = true;
                    that.errorPw = true;
                    that.errorMessage = "Invalid current password.";
                } else {
                    that.error = false;
                    that.errorPw = false;
                    // if(that.enablePasswordEdit) {
                    //     that.$root.$emit('logout');
                    // } else {
                    toast.success("User updated.");
                    that.$bvModal.hide("userProfileModal");
                    localStorage.setItem("user", `${that.loggedInUserData.first_name} ${that.loggedInUserData.last_name}`);
                    that.$root.$emit('usernameUpdated');
                    // }
                }
            });
        },

        // Hardcoded password requirement
        toggleRows() {
            this.psExpanded = !this.psExpanded;
        },
        togglePassword() {
            this.showPassword = !this.showPassword;
        },
        toggleCurrentPassword() {
            this.showCurrentPassword = !this.showCurrentPassword;
        },
        initPasswordValidator() {
            if (!this.pwValidator) {
                this.pwValidator = new passwordValidator();

                this.pwValidator.is().min(8).has().uppercase().has().lowercase().has().digits(1).has().symbols();
                this.pwValidateList = this.pwValidator.validate('', { list: true });
            }
        },
        validatePassword(password) {
            this.initPasswordValidator();
            this.pwValidateList = this.pwValidator.validate(password, { list: true });
            this.pwValidateErrorList = this.pwValidateErrorList.filter(x => this.pwValidateList.includes(x));
        },
        checkPwConditionReached(name) {
            if (this.pwValidateErrorList.includes(name)) {
                return 'err';
            }
            if (!this.pwValidateList.includes(name)) {
                return 'pass';
            }

            return 'pending'
        },
        getPwListIcon(name) {
            let code = this.checkPwConditionReached(name);
            if (code == 'err') {
                return 'fa-times';
            }
            if (code == 'pass') {
                return 'fa-check';
            }
            return 'fa-circle';
        },

        toggleEnablePasswordEdit() {
            this.enablePasswordEdit = !this.enablePasswordEdit;
            if (!this.enablePasswordEdit) {
                this.password = "";
                this.confirmPassword = "";
                this.pwValidateList = this.pwValidator.validate('', { list: true });
                this.pwValidateErrorList = [];
            }

            this.$root.$emit("userProfileTitleChange", { enablePasswordEdit: this.enablePasswordEdit });
        },

        // Notification preferences methods
        async fetchNotificationPreferences() {
            try {
                const response = await axios.get(
                    process.env.MIX_API_BASE_URL + 'notification-preferences'
                );
                this.notificationPreferences = response.data.grouped;
            } catch (error) {
                console.error('Error fetching notification preferences:', error);
                toast.error('Failed to load notification preferences');
            }
        },

        async updatePreference(pref) {
            try {
                await axios.post(
                    process.env.MIX_API_BASE_URL + 'notification-preferences',
                    {
                        global_alert_type_id: pref.id,
                        notify_mine: pref.notify_mine,
                        notify_others: pref.notify_others
                    }
                );

                toast.success('Preference saved');
            } catch (error) {
                console.error('Error updating notification preference:', error);
                toast.error('Failed to update notification preference');
            }
        },

        formatAlertType(type) {
            // Convert alert type to readable format
            return type.replace(/-/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        isOthersDisabled(pref) {
            return pref.supports_others === false;
        },
        othersTooltip(pref) {
            if (!this.isOthersDisabled(pref)) {
                return null;
            }
            return 'Not applicable for this notification type.';
        }
    },
    mounted() {
        this.initPasswordValidator();
        this.fetchNotificationPreferences();
    }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.4s ease-in;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.toggle-password {
    position: absolute;
    right: 1.5rem;
    transform: translateY(-50%);
    cursor: pointer;
}

.top-50 {
    top: 50%;
}

.top-55 {
    top: 55%;
}

.bordered-link {
    display: block;
    /* border-bottom: 1px solid #ced4da; */
}

.bordered-parent {
    margin: 0px;
    /* border: 1px solid #ced4da; */
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    border-top: none;
    border-radius: 2px;
    /* margin-top: -16px;
  margin-bottom: 1rem; */
}

a:hover {
    text-decoration: none;
}

/* Notification Preferences Styles */
.notification-preferences {
    padding: 0.5rem 0;
}

.preference-section {
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 1rem;
    background-color: #f8f9fa;
}

.section-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    color: #495057;
}

.preference-table {
    background-color: white;
    margin-bottom: 0;
}

.preference-table thead th {
    background-color: #e9ecef;
    font-weight: 600;
    border-bottom: 2px solid #dee2e6;
    font-size: 0.875rem;
}

.preference-table tbody td {
    vertical-align: middle;
    padding: 0.75rem;
}

.preference-table tbody tr td:first-child {
    width: 400px;
}
</style>
